<template>
  <teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-[15vh] backdrop-blur-sm dark:bg-black/60"
      @click="closeQuickAccess"
    >
      <div
        class="border-lightSecondaryColor bg-lightPrimaryColor dark:border-darkSecondaryColor dark:bg-darkPrimaryColor w-full max-w-2xl overflow-hidden rounded-2xl border shadow-2xl"
        tabindex="-1"
        @click.stop
      >
        <div
          class="border-lightSecondaryColor bg-lightSecondaryColor dark:border-darkSecondaryColor dark:bg-darkTertiaryColor border-b px-6 py-5"
        >
          <BaseInput
            ref="searchInputRef"
            v-model="searchQuery"
            :placeholder="$t('quickAccess.placeholder')"
            class="text-lightPrimaryTextColor placeholder-lightTertiaryColor dark:text-darkPrimaryTextColor dark:placeholder-darkSecondaryTextColor !m-0 !w-full !rounded-none !border-none !bg-transparent !px-0 !py-0 text-lg !shadow-none focus:!border-none focus:!outline-none focus:!ring-0"
          />
        </div>

        <div
          ref="resultsContainer"
          class="bg-lightPrimaryColor dark:bg-darkPrimaryColor max-h-96 overflow-y-auto"
          @mousemove="isNavigatingWithKeyboard = false"
        >
          <div v-if="filteredSearchResults.length" class="py-2">
            <QuickAccessItem
              v-for="item in filteredSearchResults.filter((i) => i.context === 'mandant')"
              :key="item.id"
              :item="item"
              :is-selected="getItemIndex(item) === selectedIndex"
              size="small"
              should-show-sections-badge
              :data-item-index="getItemIndex(item)"
              @execute="executeCurrentlySelected"
              @hover="!isNavigatingWithKeyboard && (selectedIndex = getItemIndex(item))"
            />

            <QuickAccessSeparator v-if="shouldShowSearchSeparator" :label="$t('quickAccess.globalSettings')" />

            <QuickAccessItem
              v-for="item in filteredSearchResults.filter((i) => i.context === 'admin')"
              :key="item.id"
              :item="item"
              :is-selected="getItemIndex(item) === selectedIndex"
              size="small"
              should-show-sections-badge
              :data-item-index="getItemIndex(item)"
              @execute="executeCurrentlySelected"
              @hover="!isNavigatingWithKeyboard && (selectedIndex = getItemIndex(item))"
            />
          </div>

          <div v-else-if="groupedItems.size" class="py-2">
            <div
              v-for="[key, section] in Array.from(groupedItems.entries()).filter(([, section]) => section.context === 'mandant')"
              :key="key"
              class="mb-4 last:mb-0"
            >
              <div class="border-lightTertiaryColor dark:border-darkTertiaryColor border-b px-4 py-2">
                <h3
                  class="text-lightSecondaryTextColor dark:text-darkSecondaryTextColor text-xs font-semibold uppercase tracking-wide"
                >
                  {{ section.sectionName }}
                </h3>
              </div>

              <div class="pt-1">
                <QuickAccessItem
                  v-for="item in section.quickAccessItems"
                  :key="item.id"
                  :item="item"
                  :is-selected="getItemIndex(item) === selectedIndex"
                  size="small"
                  :data-item-index="getItemIndex(item)"
                  @execute="executeCurrentlySelected"
                  @hover="!isNavigatingWithKeyboard && (selectedIndex = getItemIndex(item))"
                />
              </div>
            </div>

            <QuickAccessSeparator v-if="shouldShowGroupSeparator" :label="$t('quickAccess.globalSettings')" />

            <div
              v-for="[key, section] in Array.from(groupedItems.entries()).filter(([, section]) => section.context === 'admin')"
              :key="key"
              class="mb-4 last:mb-0"
            >
              <div class="border-lightTertiaryColor dark:border-darkTertiaryColor border-b px-4 py-2">
                <h3
                  class="text-lightSecondaryTextColor dark:text-darkSecondaryTextColor text-xs font-semibold uppercase tracking-wide"
                >
                  {{ section.sectionName }}
                </h3>
              </div>

              <div class="pt-1">
                <QuickAccessItem
                  v-for="item in section.quickAccessItems"
                  :key="item.id"
                  :item="item"
                  :is-selected="getItemIndex(item) === selectedIndex"
                  size="small"
                  :data-item-index="getItemIndex(item)"
                  @execute="executeCurrentlySelected"
                  @hover="!isNavigatingWithKeyboard && (selectedIndex = getItemIndex(item))"
                />
              </div>
            </div>
          </div>

          <QuickAccessEmptyState v-else-if="normalizedSearchQuery && !filteredSearchResults.length" />
        </div>

        <QuickAccessFooter />
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

import BaseInput from "../components/BaseInput.vue";
import { NavigationDirection } from "./enum.navigationDirection";
import type { QuickAccessItem as QuickAccessItemType } from "./model.quickAccess";
import QuickAccessEmptyState from "./QuickAccessEmptyState.vue";
import QuickAccessFooter from "./QuickAccessFooter.vue";
import QuickAccessItem from "./QuickAccessItem.vue";
import QuickAccessSeparator from "./QuickAccessSeparator.vue";
import { useQuickAccess } from "./service.quickAccess";

const quickAccessStore = useQuickAccess();
const {
  isOpen,
  searchQuery,
  selectedIndex,
  isNavigatingWithKeyboard,
  filteredSearchResults,
  normalizedSearchQuery,
  shouldShowSearchSeparator,
  groupedItems,
  shouldShowGroupSeparator,
  searchInputRef,
  visibleItems,
} = storeToRefs(quickAccessStore);
const { toggleQuickAccess, openQuickAccess, closeQuickAccess, executeCurrentlySelected } = quickAccessStore;

const resultsContainer = ref<null | HTMLDivElement>(null);

const getItemIndex = (item: QuickAccessItemType): number => {
  return visibleItems.value.indexOf(item);
};

watch(visibleItems, () => {
  selectedIndex.value = 0;
});

watch(normalizedSearchQuery, async (newQuery) => {
  if (newQuery && resultsContainer.value) {
    await nextTick();
    resultsContainer.value.scrollTop = 0;
  }
});

watch(isOpen, (newValue) => {
  if (newValue) {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  } else {
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  }
});

function handleKeydown(event: KeyboardEvent) {
  const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
  if (isShortcut) {
    event.preventDefault();
    toggleQuickAccess();
    return;
  }

  if (!isOpen.value) return;

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      handleGlobalNavigation("down");
      break;
    case "ArrowUp":
      event.preventDefault();
      handleGlobalNavigation("up");
      break;
    case "Enter":
      event.preventDefault();
      handleGlobalNavigation("enter");
      break;
    case "Escape":
      event.preventDefault();
      handleGlobalNavigation("esc");
      break;
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

let isHandlingNavigation = false;

function handleGlobalNavigation(direction: string) {
  if (isHandlingNavigation) return;

  isHandlingNavigation = true;

  try {
    switch (direction) {
      case "down":
        navigateDown();
        break;
      case "up":
        navigateUp();
        break;
      case "enter":
        executeCurrentlySelected();
        break;
      case "esc":
        closeQuickAccess();
        break;
    }
  } finally {
    setTimeout(() => {
      isHandlingNavigation = false;
    }, 50);
  }
}

function navigateDown() {
  isNavigatingWithKeyboard.value = true;
  if (selectedIndex.value < visibleItems.value.length - 1) {
    selectedIndex.value = getNextValidIndexForDirection(NavigationDirection.DOWN);
    scrollToSelectedElement();
  }
}

function navigateUp() {
  isNavigatingWithKeyboard.value = true;
  if (selectedIndex.value > 0) {
    selectedIndex.value = getNextValidIndexForDirection(NavigationDirection.UP);
    scrollToSelectedElement();
  }
}

const getNextValidIndexForDirection = (direction: NavigationDirection): number => {
  return selectedIndex.value + direction;
};

function scrollToSelectedElement() {
  const doesPreferReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  nextTick(() => {
    const selectedElement = document.querySelector(`[data-item-index="${selectedIndex.value}"]`);
    selectedElement?.scrollIntoView({
      behavior: doesPreferReducedMotion ? "auto" : "smooth",
      block: "center",
      inline: "nearest",
    });
  });
}

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleKeydown);
  closeQuickAccess();
});

defineExpose({
  openQuickAccess,
  closeQuickAccess,
  toggleQuickAccess,
});
</script>
